import { SuggestionGroupKey } from '../models/search-suggestions.model';
import { normalizeSearchText } from '../utils/search-normalize.util';

export interface UnsupportedQuestionPattern {
  test: (normalizedQuery: string) => boolean;
  /** Canonical phrase this pattern is meant to catch — the ONLY thing the test suite (this
      file's spec, and Task 4's round-trip suite) asserts against, so every pattern is provably
      reachable and demonstrable. */
  exampleQuery: string;
  /** Suggestion group shown as the "closest supported search" fallback. Omitted -> the generic
      SEARCH_EXAMPLES line is shown instead (see IntentSearchComponent, Task 7). */
  relatedGroup?: SuggestionGroupKey;
}

/** Curated, explicit comparison/superlative/causal sentence SHAPES the deterministic parser
    fundamentally cannot answer — no amount of vocabulary matching fixes "which is worst" or "why
    is X low", because those ask for computation/ranking/explanation across records, not a filter.
    Static and small on purpose (no NLP — same "no NLP" boundary the parent spec set). */
export const UNSUPPORTED_QUESTION_PATTERNS: UnsupportedQuestionPattern[] = [
  {
    test: (q) => /\bwhich\b.*\b(worst|best|most|least|highest|lowest)\b/.test(q),
    exampleQuery: 'which department has the worst backlog',
    relatedGroup: 'department',
  },
  {
    test: (q) => /\bwho\b.*\b(most|least)\b/.test(q),
    exampleQuery: 'who has the most pending licenses',
    relatedGroup: 'people',
  },
  {
    test: (q) => /\bwhy\b.*\b(low|high|bad|good|poor)\b/.test(q),
    exampleQuery: 'why is closure score low',
  },
  {
    test: (q) => /\bcompare\b/.test(q),
    exampleQuery: 'compare maharashtra and gujarat',
  },
  {
    test: (q) => /\bwhat\b.*\breason\b/.test(q),
    exampleQuery: 'what is the reason for the delay',
  },
];

/** `query` goes through the SAME normalization every vocabulary entry and typed token already
    uses (case-fold, strip diacritics, collapse whitespace/punctuation) so patterns never need to
    handle raw casing/punctuation themselves. */
export function detectUnsupportedQuestion(query: string): UnsupportedQuestionPattern | null {
  const normalized = normalizeSearchText(query);
  return UNSUPPORTED_QUESTION_PATTERNS.find((pattern) => pattern.test(normalized)) ?? null;
}
